import { useEffect, useState } from "react";
import HomeView from "./view";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "../../redux/reducers/Models";
import { debtsPulling, requestDebts, setUser, clearUser, authorize } from "../../redux/ducks/user";
import { clearCustomer, setCustomer } from "../../redux/ducks/customer";
import { PropsView } from "./Models";
import { Debts, getFormattedDebts } from "@/utils/transform";
import { getIsValidString } from "@/utils/functions";
import useRedirect from "../../hooks/useRedirect";
import { useNavigate } from "react-router-dom";

export default function Home() {
  useRedirect("home");

  const navigate = useNavigate();
  let TIMEOUT = 2000;
  const dispatch = useDispatch();
  const user = useSelector((state: ReducersTypes & {
    userReducer: ReducersTypes['userReducer'] & {
      cart: PropsView['selectables'];
      debts: PropsView['items'];
    }
  }) => state.userReducer);

  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  const [isLoading, setIsLoading] = useState(false);
  
  const goBack = () => navigate(-1);
  const goGetout = () => {
    dispatch(clearCustomer());
    dispatch(clearUser());
    navigate('/');
  };
  const goToVehicles = () => navigate('/vehicles');
  const goToProfile = () => navigate("/profile");

  const handleDebtsPulling = (success: boolean, code?: string | Debts[]) => {
    if (!success) {
      if (code === "again") {
        setTimeout(() => {
          TIMEOUT = TIMEOUT*2;
          startPulling();
        }, TIMEOUT);
        return;
      }
      setIsLoading(false);
      dispatch(
        setUser({
          debts: [],
          cart: undefined,
        })
      );
      return;
    }

    const formattedDebts = getFormattedDebts(code as Debts[]);
    
    dispatch(
      setUser({
        debts: formattedDebts,
        cart: formattedDebts.map(item => ({ name: item.id, checked: true })),
      })
    );
    setIsLoading(false);
  }

  const startPulling = (requestId?: string) => {
    const currentRequestId = getIsValidString(requestId as string) ? requestId as string : user?.requestId as string;
    
    if (!getIsValidString(currentRequestId)) {
      return;
    }
    
    if (!isLoading) setIsLoading(true);

    dispatch(
      debtsPulling({
        requestId: currentRequestId,
        callback: handleDebtsPulling,
      })
    );
  }

  const onSearchNewPlate = (newPlate: string) => {
    const cleanPlate = newPlate.toUpperCase().replace(/[^A-Z0-9]/g, "");
    
    if (!cleanPlate || cleanPlate.length < 7) {
      return;
    }

    const isLogged = Boolean(customer?.accessToken);

    dispatch(
      setUser({
        plate: cleanPlate,
        requestId: undefined,
        debts: [],
        cart: undefined,
      })
    );

    dispatch(
      setCustomer({
        plate: cleanPlate,
      })
    );
    
    setIsLoading(true);

    if (!isLogged) {
      dispatch(
        authorize({
          plate: cleanPlate,
          token: '',
          callback: (success: boolean, errorMessage?: string) => {
            if (!success) {
              setIsLoading(false);
              dispatch(
                setUser({
                  debts: [],
                  cart: undefined,
                })
              );
              return;
            }

            setTimeout(() => {
              dispatch(
                requestDebts({
                  plate: cleanPlate,
                  callback: (success: boolean, code?: string) => {
                  if (!success) {
                    setIsLoading(false);
                    dispatch(
                      setUser({
                        debts: [],
                        cart: undefined,
                      })
                    );
                    return;
                  }

                  if (getIsValidString(code as string)) {
                    dispatch(
                      setUser({
                        requestId: code,
                      })
                    );
                    
                    setIsLoading(true);
                    dispatch(
                      debtsPulling({
                        requestId: code as string,
                        callback: handleDebtsPulling,
                      })
                    );
                  }
                },
              })
            );
            }, 100);
          },
        })
      );
    } else {
      dispatch(
        requestDebts({
          plate: cleanPlate,
          callback: (success: boolean, code?: string) => {
            if (!success) {
              setIsLoading(false);
              dispatch(
                setUser({
                  debts: [],
                  cart: undefined,
                })
              );
              return;
            }

            if (getIsValidString(code as string)) {
              console.log('[onSearchNewPlate] requestDebts sucesso, requestId recebido:', code);
              dispatch(
                setUser({
                  requestId: code,
                })
              );
              
              setIsLoading(true);
              console.log('[onSearchNewPlate] Iniciando debtsPulling com requestId:', code);
              dispatch(
                debtsPulling({
                  requestId: code as string,
                  callback: handleDebtsPulling,
                })
              );
            } 
          },
        })
      );
    }
  };

  const onChange: PropsView['onChange'] = (e) => {
    const { name, checked } = e.target;

    if (name === "all") {
      dispatch(
        setUser({
          cart: checked ? user?.debts?.map(item => ({ name: item.id, checked: true })) : null,
        })
      );
      return;
    }

    const updatedSelectables = user?.cart?.filter(item => item.name !== name) || [];

    if (checked) {
      updatedSelectables.push({ name, checked });
    }

    dispatch(
      setUser({
        cart: updatedSelectables.length ? updatedSelectables : null
      })
    );
  };

  const handleRequestDebts = (success: boolean, code?: string) => {
    if (!success) {
      setIsLoading(false);
      dispatch(
        setUser({
          debts: [],
        })
      );
      return;
    }

    dispatch(
      setUser({
        requestId: code,
      })
    );
  }

  const getDebts = () => {
    if (isLoading) return;
    if (getIsValidString(user?.requestId as string)) return;

    setIsLoading(true);
    dispatch(
      requestDebts({
        plate: user?.plate as string,
        callback: handleRequestDebts,
      })
    );
  }

  useEffect(() => {
    if (getIsValidString(user?.requestId as string) && isLoading) {
      startPulling(user?.requestId as string);
    }
  }, [user?.requestId]);

  useEffect(() => {
    getDebts();
    if (getIsValidString(user?.requestId as string) && !isLoading) {
      startPulling(user?.requestId as string);
    }
  }, []);
  
  return (
    <HomeView 
      goToVehicles={goToVehicles}
      items={user?.debts || []}
      plate={user?.plate || ""}
      isLoading={isLoading}
      onChange={onChange}
      selectables={user?.cart || null}
      goBack={goBack}
      goToProfile={goToProfile}
      goGetout={goGetout}
      onSearchNewPlate={onSearchNewPlate}
    />
  );
}
